/**
 * adminRoadmapDragGuard — whole-row drag for the admin Roadmap List view.
 *
 * Every List row is draggable to reorder; a plain click on any in-row control
 * still works the control. That distinction is owned entirely by
 * RowReorderPointerSensor's activator: it refuses to start a drag when the
 * pointerdown lands inside an interactive element, so a control click is never
 * intercepted — the sensor doesn't even activate, so there is no preventDefault
 * and no pending-drag state to interfere with the native control.
 *
 * Every control the row renders is a NATIVE element — <button> (title,
 * TODAY/TOMORROW/NEXT-UP toggles, pin, delete) and <select> (the five inline
 * status/category/severity/visibility/assignee dropdowns) — so one `closest()`
 * over native tag selectors covers them all. `[data-no-dnd]` is an escape hatch
 * for any future custom (non-native) control.
 *
 * ⚠️ The selector must NOT include [role="button"]. dnd-kit's useSortable
 * spreads {...attributes} onto the row wrapper, which sets role="button" for
 * keyboard drag. A [role="button"] clause would make `closest()` match that
 * wrapper on a row-background press and block EVERY reorder. Native tags +
 * [data-no-dnd] only. (Pinned in adminRoadmapDragGuard.test.ts.)
 */
import { PointerSensor } from '@dnd-kit/core';

export const INTERACTIVE_DRAG_SELECTOR =
  'button, select, input, textarea, a, [data-no-dnd]';

/**
 * True when a pointerdown target sits inside an interactive control — the row
 * must NOT begin a drag in that case. Duck-types `closest` rather than using
 * `instanceof Element` so it also runs under the repo's node jest env (where
 * `Element` is undefined). Defensive: false (drag eligible) for a null target
 * or one without `closest`. Real pointerdown targets are always Elements —
 * including SVG icons inside a <button>, since SVGElement has `closest` and it
 * walks up to the enclosing <button>.
 */
export function isInteractiveDragTarget(target: EventTarget | null): boolean {
  const el = target as Element | null;
  if (!el || typeof el.closest !== 'function') return false;
  return el.closest(INTERACTIVE_DRAG_SELECTOR) !== null;
}

/**
 * PointerSensor that starts a drag from anywhere on a row EXCEPT its controls.
 * Mirrors dnd-kit's default activator (primary pointer + left button) and adds
 * the interactive-target guard before activating. Paired with an 8px activation
 * distance at the call site so a press-and-release on the row background is a
 * no-op, not an accidental one-pixel reorder.
 *
 * The `typeof PointerSensor.activators` annotation contextually types the
 * handler params (event: React PointerEvent, options carrying onActivation) and
 * makes this static a type-identical override of the base.
 */
export class RowReorderPointerSensor extends PointerSensor {
  static activators: typeof PointerSensor.activators = [
    {
      eventName: 'onPointerDown',
      handler: ({ nativeEvent: event }, { onActivation }) => {
        if (!event.isPrimary || event.button !== 0) return false;
        if (isInteractiveDragTarget(event.target)) return false;
        onActivation?.({ event });
        return true;
      },
    },
  ];
}
